import { clsx } from "clsx";
import { useContext, useRef } from "react";
import {
  type AriaCheckboxGroupItemProps,
  type Placement,
  useCheckboxGroupItem,
  useFocusRing,
  VisuallyHidden,
} from "react-aria";

import { useTooltip } from "../../hooks/useTooltip.ts";
import formStyle from "../../styles/form.module.css";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import styleLabel from "../Label/Label.module.css";
import { VStack } from "../Stack/VStack.tsx";
import { AnimatedTooltip } from "../Tooltip/AnimatedTooltip.tsx";
import style from "./CheckBox.module.css";
import { CheckboxGroupContext } from "./settings.ts";

interface CheckboxProps extends AriaCheckboxGroupItemProps {
  description?: string;
  expandToFill?: boolean;
  tooltip?: string;
  placement?: Placement;
}

export const Checkbox = (props: CheckboxProps) => {
  const state = useContext(CheckboxGroupContext);
  const inputRef = useRef(null);
  const labelRef = useRef(null);
  const { description, expandToFill, tooltip, placement = "bottom start", children, isIndeterminate } = props;

  const { inputProps, isDisabled, isSelected } = useCheckboxGroupItem(props, state, inputRef);
  const { isFocusVisible, focusProps } = useFocusRing();
  const { triggerProps, tooltipProps, state: tooltipState } = useTooltip(labelRef);

  return (
    <label className={clsx(style.input, { [style.disabled]: isDisabled })} ref={labelRef} {...triggerProps}>
      <VisuallyHidden>
        <input {...inputProps} {...focusProps} ref={inputRef} disabled={isDisabled} />
      </VisuallyHidden>
      <div
        className={clsx(style.checkbox, {
          [style.selected]: isSelected,
          [style.focus]: isFocusVisible,
          [style.error]: state.isInvalid,
        })}
      >
        {isSelected && (
          <SymbolIcon className={style.check} icon={isIndeterminate ? "horizontal_rule" : "check"} size={16} />
        )}
      </div>
      {children != undefined && (
        <VStack spacing={0} style={{ ...(expandToFill ? { flex: 1 } : {}) }}>
          <div className={styleLabel.label}>{children}</div>
          {description != undefined && <div className={formStyle.description}>{description}</div>}
        </VStack>
      )}
      {tooltip && (
        <AnimatedTooltip
          targetRef={labelRef}
          domProps={tooltipProps}
          title={tooltip}
          state={tooltipState}
          placement={placement}
        />
      )}
    </label>
  );
};
